﻿import React, { useEffect, useRef, useState } from 'react';
import { createBrowserRouter, RouterProvider, useNavigate, useLocation, Outlet } from "react-router-dom";

import './App.css';

// Import authentication components
import { AuthProvider } from './contexts/AuthContext';
import { ContextProvider } from './contexts/ContextProvider';
import { XMPPProvider } from './contexts/XMPPContext';
import { RightRailProvider, useRightRail } from './contexts/RightRailContext';
import RightRail from './components/layout/RightRail';
import PatientSidebarPanel from './components/layout/PatientSidebarPanel';
import TasksSidebarPanel from './components/layout/TasksSidebarPanel';
import UserFilesSidebarPanel from './components/layout/UserFilesSidebarPanel';
import ProtectedRoute from './components/ProtectedRoute';
import SessionLockOverlay from './components/SessionLockOverlay';

import { Navbar, Footer, Sidebar, ThemeSettings } from './components';
import ClinicalChatPanel from './components/chat/ClinicalChatPanel';
import { FaUser, FaTasks, FaFolder } from 'react-icons/fa';
import AdminNavbar from './admin/components/AdminNavbar';
import AdminSidebar from './admin/components/AdminSidebar';
import { useStateContext } from './contexts/ContextProvider';
import { appRoutes } from './routes';
import SyncfusionPlainDebugPage from './pages/Debug/SyncfusionPlainDebugPage';

// Loading overlay component
const LoadingOverlay: React.FC = () => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
    <div className="flex flex-col items-center">
      <div className="loading loading-spinner loading-lg text-primary"></div>
      <p className="mt-4 text-white">Loading...</p>
    </div>
  </div>
);

// Layout component
const Layout: React.FC = () => {
  const { sidebarState, themeSettings, currentMode, selectedClientId, selectedFacilityId } = useStateContext();
  const { activePanelId, registerButton, unregisterButton } = useRightRail();

  // Resizable chat panel
  const [chatWidth, setChatWidth] = useState(320);
  const [isDraggingChat, setIsDraggingChat] = useState(false);
  const chatDragRef = useRef<{ startX: number; startW: number } | null>(null);

  const onChatDragMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    chatDragRef.current = { startX: e.clientX, startW: chatWidth };
    setIsDraggingChat(true);

    const onMove = (ev: MouseEvent) => {
      if (!chatDragRef.current) return;
      // Drag handle is on the left edge; moving left widens the panel
      const delta = chatDragRef.current.startX - ev.clientX;
      const newW = Math.min(600, Math.max(280, chatDragRef.current.startW + delta));
      setChatWidth(newW);
    };

    const onUp = () => {
      chatDragRef.current = null;
      setIsDraggingChat(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Register global rail buttons
  useEffect(() => {
    registerButton({
      id: 'patient-sidebar',
      icon: <FaUser className="text-sm" />,
      label: 'Patient Lookup',
      order: 5,
    });
    registerButton({
      id: 'tasks-sidebar',
      icon: <FaTasks className="text-sm" />,
      label: 'My Tasks',
      order: 15,
    });
    registerButton({
      id: 'user-files',
      icon: <FaFolder className="text-sm" />,
      label: 'My Files',
      order: 20,
    });
    return () => {
      unregisterButton('patient-sidebar');
      unregisterButton('tasks-sidebar');
      unregisterButton('user-files');
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const isAdminRoute = location.pathname === '/admin' || location.pathname.startsWith('/admin/');

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const handleNavigationStart = () => {
      setIsLoading(true);
    };

    const handleNavigationEnd = () => {
      setIsLoading(false);
    };

    window.addEventListener('NavigationStart', handleNavigationStart, { signal });
    window.addEventListener('NavigationEnd', handleNavigationEnd, { signal });

    return () => {
      controller.abort();
      window.removeEventListener('NavigationStart', handleNavigationStart);
      window.removeEventListener('NavigationEnd', handleNavigationEnd);
    };
  }, [navigate, location]);

  return (
    <ProtectedRoute>
      <div className={currentMode === 'Dark' ? 'dark' : ''}>
        {isLoading && <LoadingOverlay />}
        <div className="flex h-screen overflow-hidden bg-base-100">
          <div className={`${sidebarState === 'hidden' ? 'w-0' : sidebarState === 'collapsed' ? 'w-16' : 'w-64'} transition-width duration-300 ${isAdminRoute ? 'bg-base-100' : 'bg-primary'}`}>
            {isAdminRoute ? <AdminSidebar /> : <Sidebar />}
          </div>

          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="bg-base-100">
              {isAdminRoute ? <AdminNavbar /> : <Navbar />}
            </div>

            <div className="flex-1 flex overflow-hidden">
              {/* Page content */}
              <div className="flex-1 overflow-auto">
                {themeSettings && <ThemeSettings />}
                <div key={`${selectedClientId}:${selectedFacilityId}`} className="h-full">
                  <Outlet />
                </div>
              </div>

              {!isAdminRoute && (
                <>
                  {/* Patient Lookup panel */}
                  <div className={`flex-shrink-0 overflow-hidden transition-all duration-300 bg-base-100 border-l border-base-300 ${activePanelId === 'patient-sidebar' ? 'w-80' : 'w-0'}`}>
                    <div className="w-80 h-full flex flex-col">
                      <div className="px-3 py-2 border-b border-base-300 bg-base-200 text-sm font-semibold flex-shrink-0">
                        Patient Lookup
                      </div>
                      <div className="flex-1 overflow-hidden">
                        <PatientSidebarPanel />
                      </div>
                    </div>
                  </div>

                  {/* Tasks panel */}
                  <div className={`flex-shrink-0 overflow-hidden transition-all duration-300 bg-base-100 border-l border-base-300 ${activePanelId === 'tasks-sidebar' ? 'w-80' : 'w-0'}`}>
                    <div className="w-80 h-full flex flex-col">
                      <div className="px-3 py-2 border-b border-base-300 bg-base-200 text-sm font-semibold flex-shrink-0">
                        My Tasks
                      </div>
                      <div className="flex-1 overflow-hidden">
                        <TasksSidebarPanel />
                      </div>
                    </div>
                  </div>

                  {/* User Files panel */}
                  <div className={`flex-shrink-0 overflow-hidden transition-all duration-300 bg-base-100 border-l border-base-300 ${activePanelId === 'user-files' ? 'w-80' : 'w-0'}`}>
                    <div className="w-80 h-full flex flex-col">
                      <div className="px-3 py-2 border-b border-base-300 bg-base-200 text-sm font-semibold flex-shrink-0">
                        My Files
                      </div>
                      <div className="flex-1 overflow-hidden">
                        <UserFilesSidebarPanel />
                      </div>
                    </div>
                  </div>

                  {/* Chat panel — resizable via drag handle on left edge */}
                  <div
                    className={`flex-shrink-0 overflow-hidden relative bg-base-100 border-l border-base-300 ${!isDraggingChat ? 'transition-all duration-300' : ''}`}
                    style={{ width: activePanelId === 'chat' ? chatWidth : 0 }}
                  >
                    {/* Drag handle */}
                    <div
                      className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60 z-10 transition-colors"
                      onMouseDown={onChatDragMouseDown}
                    />
                    <div className="h-full flex flex-col" style={{ width: chatWidth }}>
                      <div className="px-3 py-2 border-b border-base-300 bg-base-200 text-sm font-semibold flex-shrink-0">
                        Clinical Chat
                      </div>
                      <div className="flex-1 overflow-hidden">
                        <ClinicalChatPanel />
                      </div>
                    </div>
                  </div>

                  {/* Icon rail — always 48 px */}
                  <RightRail />
                </>
              )}
            </div>
          </div>
        </div>

      </div>
    </ProtectedRoute>
  );
};

const router = createBrowserRouter(
  [
    {
      path: "/debug/syncfusion-plain",
      element: <SyncfusionPlainDebugPage />,
    },
    {
      path: "/",
      element: <Layout />,
      children: appRoutes,
      HydrateFallback: () => <LoadingOverlay />,
    },
  ] as any, {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    } as any,
  }
);

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ContextProvider>
        <XMPPProvider>
          <RightRailProvider>
            <RouterProvider router={router} />
            <SessionLockOverlay />
          </RightRailProvider>
        </XMPPProvider>
      </ContextProvider>
    </AuthProvider>
  );
};

export default App;